import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Trash2, Edit2, Lock } from 'lucide-react';

const ExperimentDetailsModal = ({
  experiment,
  onClose,
  onEdit,
  onDelete,
}) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isCreator, setIsCreator] = useState(false);
  const [showAccessError, setShowAccessError] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      setIsCreator(user?.id === experiment.created_by);
    };
    fetchUser();
  }, [experiment.created_by]);
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${dateStr} at ${timeStr}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Success':
        return 'bg-green-500/20 text-green-400';
      case 'Failure':
        return 'bg-red-500/20 text-red-400';
      case 'In Progress':
        return 'bg-blue-500/20 text-blue-400';
      case 'Pending':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-[#1a1f2e] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 border border-white/10">
        {/* Header */}
        <div className="sticky top-0 bg-[#1a1f2e] border-b border-white/10 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">Experiment Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Title and Status */}
          <div>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {experiment.title}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span>{formatDateTime(experiment.created_at)}</span>
                </div>
              </div>
              <span className={`px-4 py-2 rounded-md text-sm font-semibold uppercase whitespace-nowrap ${getStatusColor(experiment.status)}`}>
                {experiment.status}
              </span>
            </div>
          </div>

          {/* Meta Information */}
          <div className="bg-[#0a0e1a] rounded-lg p-4 space-y-3 border border-white/10">
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1">Category</p>
              <p className="text-base text-indigo-400 font-semibold">{experiment.category}</p>
            </div>
            {experiment.linked_test && (
              <div>
                <p className="text-xs font-medium text-gray-400 mb-1">Linked Test</p>
                <p className="text-base text-gray-200 font-medium">@ {experiment.linked_test}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1">Created By</p>
              <div className="space-y-1">
                <p className="text-base text-gray-200 font-medium">{experiment.creator_name}</p>
                <p className="text-sm text-gray-400">{experiment.creator_email}</p>
              </div>
            </div>
          </div>

          {/* Hypothesis */}
          {experiment.hypothesis && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Hypothesis
              </label>
              <div className="bg-[#0a0e1a] rounded-lg p-4 border border-white/10">
                <p className="text-gray-300 text-sm leading-relaxed">
                  {experiment.hypothesis}
                </p>
              </div>
            </div>
          )}

          {/* Method */}
          {experiment.method && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Method (How are you testing it?)
              </label>
              <div className="bg-[#0a0e1a] rounded-lg p-4 border border-white/10">
                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                  {experiment.method}
                </p>
              </div>
            </div>
          )}

          {/* Expected vs Actual Results */}
          <div className="grid grid-cols-2 gap-4">
            {experiment.expected_result && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Expected Result
                </label>
                <div className="bg-[#0a0e1a] rounded-lg p-4 border border-white/10">
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {experiment.expected_result}
                  </p>
                </div>
              </div>
            )}
            {experiment.actual_result && (
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Actual Result
                </label>
                <div className="bg-[#0a0e1a] rounded-lg p-4 border border-white/10">
                  <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {experiment.actual_result}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Lesson Learned */}
          {experiment.lesson_learned && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                Lesson Learned
              </label>
              <div className="bg-[#0a0e1a] rounded-lg p-4 border border-white/10">
                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                  {experiment.lesson_learned}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t border-white/10">
            {isCreator ? (
              <>
                <button
                  onClick={() => onEdit()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
                >
                  <Edit2 size={16} />
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this experiment?')) {
                      onDelete(experiment.id);
                      onClose();
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors ml-auto"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowAccessError(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600/20 text-gray-400 rounded-lg ml-auto cursor-not-allowed"
              >
                <Lock size={16} />
                <span className="text-sm">No access to edit or delete</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Access Denied Modal */}
      {showAccessError && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-[#1a1f2e] rounded-lg shadow-xl w-full max-w-sm m-4 border border-red-500/30 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Lock size={24} className="text-red-400" />
              <h3 className="text-xl font-semibold text-red-400">No Access</h3>
            </div>
            <p className="text-gray-300 mb-6">
              You do not have permission to delete this experiment. Only the creator can delete it.
            </p>
            <button
              onClick={() => setShowAccessError(false)}
              className="w-full px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperimentDetailsModal;
